import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import App from './App.tsx'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ThemeProvider } from './components/ThemeProvider'
import { loadTheme } from './lib/theme/themeLoader'
import { applyThemeColors, applyThemeMode, getEffectiveThemeMode } from './lib/theme/themeUtils'

/**
 * Configure React Query client with sensible defaults
 * - 5 minute stale time for queries
 * - Single retry on failure
 * - Refetch on window focus disabled for chat app
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

/**
 * Gets customer ID from localStorage if available
 * @returns Customer ID or undefined
 */
const getCustomerId = (): string | undefined => {
  try {
    return localStorage.getItem('parlant_customer_id') || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Applies theme immediately before React renders to prevent flash of unstyled content
 */
const applyInitialTheme = (): void => {
  const customerId = getCustomerId();
  const theme = loadTheme(customerId);
  const effectiveMode = getEffectiveThemeMode();
  const colors = effectiveMode === 'dark' ? theme.dark : theme.light;
  applyThemeColors(colors);
  applyThemeMode(effectiveMode);
};

// Apply theme synchronously before React renders
applyInitialTheme();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary
        errorTitle="Application Error"
        errorMessage="The Virtual Chat application encountered an unexpected error"
      >
        <ThemeProvider customerId={getCustomerId()}>
          <App />
        </ThemeProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>,
)
